import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Space,
  Timeline,
  Tabs,
  Input,
  Spin,
  App,
  Typography,
  Collapse,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  UserOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import {
  CardRenewalDraftWithUser,
  CardRenewalDraftStatus,
  CardRenewalStateLog,
  PayCenterHistoryEntry,
} from '@mygate/common';
import { useState, useEffect } from 'react';
import { cardRenewalService } from '@/services/cardRenewal';
import { formatDateTime, formatDate } from '@/utils/date';
import { UserDetailModal } from './UserDetailModal';

interface CardRenewalDetailModalProps {
  visible: boolean;
  draft: CardRenewalDraftWithUser | null;
  onClose: () => void;
  onActionComplete: () => void;
  loading?: boolean;
}

export function CardRenewalDetailModal({
  visible,
  draft,
  onClose,
  onActionComplete,
  loading = false,
}: CardRenewalDetailModalProps) {
  const { modal, message } = App.useApp();
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [paycenterHistory, setPaycenterHistory] = useState<PayCenterHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch PayCenter history when modal opens and cardOrderId exists
  useEffect(() => {
    async function fetchPaycenterHistory() {
      if (!visible || !draft?.cardOrderId) {
        setPaycenterHistory([]);
        return;
      }

      setHistoryLoading(true);
      try {
        const history = await cardRenewalService.getCardOrderHistory(draft.cardOrderId);
        setPaycenterHistory(history);
      } catch (error) {
        console.error('Failed to fetch PayCenter history:', error);
        setPaycenterHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    }

    fetchPaycenterHistory();
  }, [visible, draft?.cardOrderId]);

  if (!draft) {
    return null;
  }

  const getStatusColor = (status: CardRenewalDraftStatus) => {
    switch (status) {
      case CardRenewalDraftStatus.DRAFT:
        return 'default';
      case CardRenewalDraftStatus.APPROVED:
        return 'processing';
      case CardRenewalDraftStatus.PROCESSING:
        return 'processing';
      case CardRenewalDraftStatus.COMPLETED:
        return 'success';
      case CardRenewalDraftStatus.REJECTED:
        return 'error';
      case CardRenewalDraftStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: CardRenewalDraftStatus) => {
    switch (status) {
      case CardRenewalDraftStatus.DRAFT:
        return <ClockCircleOutlined />;
      case CardRenewalDraftStatus.APPROVED:
        return <CheckCircleOutlined />;
      case CardRenewalDraftStatus.PROCESSING:
        return <SyncOutlined spin />;
      case CardRenewalDraftStatus.COMPLETED:
        return <CheckCircleOutlined />;
      case CardRenewalDraftStatus.REJECTED:
        return <CloseCircleOutlined />;
      case CardRenewalDraftStatus.CANCELLED:
        return <StopOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const handleApprove = async () => {
    modal.confirm({
      title: 'Approve Card Renewal',
      content: 'Are you sure you want to approve this card renewal request?',
      onOk: async () => {
        setActionLoading(true);
        try {
          await cardRenewalService.approveDraft(draft.id);
          message.success('Card renewal approved successfully');
          onActionComplete();
          onClose();
        } catch (error: unknown) {
          const err = error as Error;
          message.error(err.message || 'Failed to approve card renewal');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.error('Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      await cardRenewalService.rejectDraft(draft.id, rejectReason);
      message.success('Card renewal rejected');
      onActionComplete();
      onClose();
    } catch (error: unknown) {
      const err = error as Error;
      message.error(err.message || 'Failed to reject card renewal');
    } finally {
      setActionLoading(false);
      setShowRejectInput(false);
      setRejectReason('');
    }
  };

  const formatAddress = () => {
    if (!draft.deliveryAddress) return 'Not set';
    const addr = draft.deliveryAddress;
    const parts = [addr.street, addr.address2, `${addr.postalcode} ${addr.city}`, addr.country].filter(Boolean);
    return parts.join(', ');
  };

  const formatNameOnCard = () => {
    if (!draft.nameOnCard) return 'Not set';
    return [draft.nameOnCard.line1, draft.nameOnCard.line2].filter(Boolean).join(' / ');
  };

  return (
    <Modal
      title="Card Renewal Details"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        draft.status === CardRenewalDraftStatus.DRAFT && (
          <>
            {!showRejectInput ? (
              <>
                <Button key="reject" danger onClick={() => setShowRejectInput(true)} loading={actionLoading}>
                  Reject
                </Button>
                <Button key="approve" type="primary" onClick={handleApprove} loading={actionLoading}>
                  Approve
                </Button>
              </>
            ) : (
              <Space wrap key="reject-input">
                <Input
                  placeholder="Rejection reason"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  style={{ width: 300 }}
                />
                <Button
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button danger onClick={handleReject} loading={actionLoading}>
                  Confirm Reject
                </Button>
              </Space>
            )}
          </>
        ),
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs defaultActiveKey="details">
          <Tabs.TabPane tab="Details" key="details">
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}>
              <Descriptions.Item label="Renewal ID" span="filled">
                {draft.id}
              </Descriptions.Item>
              <Descriptions.Item label="User" span={1}>
                <Button type="link" onClick={() => setUserModalVisible(true)} style={{ padding: 0, height: 'auto' }}>
                  <Space wrap>
                    <UserOutlined />
                    <Typography.Text strong style={{ color: '#1890ff' }}>
                      {draft.user
                        ? `${draft.user.firstName || ''} ${draft.user.lastName || ''}`.trim() ||
                          draft.user.phoneNumber ||
                          draft.userId.slice(-8)
                        : draft.userId.slice(-8)}
                    </Typography.Text>
                  </Space>
                </Button>
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={1}>
                <Tag icon={getStatusIcon(draft.status)} color={getStatusColor(draft.status)}>
                  {draft.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Card Expiration" span={1}>
                <strong>{formatDate(draft.cardExpirationDate)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Created At" span={1}>
                {formatDateTime(draft.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery Address" span="filled">
                {formatAddress()}
              </Descriptions.Item>
              <Descriptions.Item label="Name on Card" span="filled">
                {formatNameOnCard()}
              </Descriptions.Item>
              {draft.approvedBy && (
                <>
                  <Descriptions.Item label="Approved By" span={1}>
                    {draft.approvedBy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approved At" span={1}>
                    {draft.approvedAt && formatDateTime(draft.approvedAt)}
                  </Descriptions.Item>
                </>
              )}
              {draft.rejectionReason && (
                <Descriptions.Item label="Rejection Reason" span="filled">
                  <span style={{ color: '#ff4d4f' }}>{draft.rejectionReason}</span>
                </Descriptions.Item>
              )}
              {draft.status === CardRenewalDraftStatus.COMPLETED && draft.updatedAt && (
                <Descriptions.Item label="Completed At" span={1}>
                  {formatDateTime(draft.updatedAt)}
                </Descriptions.Item>
              )}
              {draft.cardOrderId && (
                <Descriptions.Item label="Card Order ID" span="filled">
                  <code>{draft.cardOrderId}</code>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Tabs.TabPane>

          <Tabs.TabPane tab="History" key="history">
            <Typography.Title level={5}>State Transitions</Typography.Title>
            {draft.stateTransitions && draft.stateTransitions.length > 0 ? (
              <Timeline mode="left">
                {draft.stateTransitions.map((transition: CardRenewalStateLog, index: number) => (
                  <Timeline.Item
                    key={index}
                    color={getStatusColor(transition.toStatus)}
                    label={formatDateTime(transition.changedAt)}
                  >
                    <p>
                      <strong>
                        {transition.fromStatus || 'Initial'} → {transition.toStatus}
                      </strong>
                    </p>
                    {transition.reason && <p>Reason: {transition.reason}</p>}
                    {transition.changedBy && <p>By: {transition.changedBy}</p>}
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <p style={{ color: '#8c8c8c', textAlign: 'center' }}>No state transitions recorded</p>
            )}

            {draft.cardOrderId && (
              <>
                <Divider />
                <Typography.Title level={5}>
                  <ApiOutlined /> PayCenter API History
                </Typography.Title>
                {historyLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin />
                  </div>
                ) : paycenterHistory.length > 0 ? (
                  <Timeline mode="left">
                    {paycenterHistory.map((entry: PayCenterHistoryEntry, index: number) => (
                      <Timeline.Item
                        key={index}
                        color={entry.success ? 'green' : 'red'}
                        label={formatDateTime(entry.timestamp)}
                      >
                        <p>
                          <strong>{entry.action}</strong>
                          <Tag color={entry.success ? 'success' : 'error'} style={{ marginLeft: 8 }}>
                            HTTP {entry.httpStatus}
                          </Tag>
                        </p>
                        <p style={{ color: '#8c8c8c', fontSize: '12px' }}>{entry.endpoint}</p>
                        {entry.errorMessage && <p style={{ color: '#ff4d4f' }}>Error: {entry.errorMessage}</p>}
                        <Collapse
                          size="small"
                          items={[
                            {
                              key: '1',
                              label: 'Response Body',
                              children: (
                                <pre
                                  style={{
                                    background: '#f5f5f5',
                                    padding: 8,
                                    borderRadius: 4,
                                    fontSize: 11,
                                    maxHeight: 200,
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {(() => {
                                    try {
                                      return JSON.stringify(JSON.parse(entry.responseBody), null, 2);
                                    } catch {
                                      return entry.responseBody;
                                    }
                                  })()}
                                </pre>
                              ),
                            },
                          ]}
                        />
                      </Timeline.Item>
                    ))}
                  </Timeline>
                ) : (
                  <p style={{ color: '#8c8c8c', textAlign: 'center' }}>No PayCenter API history recorded</p>
                )}
              </>
            )}
          </Tabs.TabPane>
        </Tabs>
      )}

      <UserDetailModal userId={draft.userId} visible={userModalVisible} onClose={() => setUserModalVisible(false)} />
    </Modal>
  );
}
